import React, { useState } from "react";
import { Dropdown, Button, Space, Typography, Badge } from "antd";
import type { MenuProps } from "antd";
import {
  ThunderboltOutlined,
  UserAddOutlined,
  ApiOutlined,
  MailOutlined,
  FileAddOutlined,
  TeamOutlined,
  KeyOutlined,
  BellOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import Card from "./Card";

const { Text } = Typography;

interface QuickAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  description?: string;
  onClick: () => void;
  badge?: number;
  disabled?: boolean;
  category?: string;
}

interface QuickActionsMenuProps {
  actions?: QuickAction[];
  buttonText?: string;
  buttonType?: "primary" | "default" | "dashed" | "link" | "text";
  buttonSize?: "small" | "middle" | "large";
  placement?: "bottomLeft" | "bottomRight" | "topLeft" | "topRight";
}

const QuickActionsMenu: React.FC<QuickActionsMenuProps> = ({
  actions,
  buttonText = "Quick Actions",
  buttonType = "primary",
  buttonSize = "middle",
  placement = "bottomRight",
}) => {
  const [open, setOpen] = useState(false);
  const noop = () => undefined;

  const defaultActions: QuickAction[] = [
    {
      key: "add-user",
      label: "Add User",
      icon: <UserAddOutlined />,
      description: "Create a new user account",
      category: "Users",
      onClick: noop,
    },
    {
      key: "add-integration",
      label: "Add Integration",
      icon: <ApiOutlined />,
      description: "Connect a new service",
      category: "Integrations",
      onClick: noop,
    },
    {
      key: "send-notification",
      label: "Send Notification",
      icon: <BellOutlined />,
      description: "Send a system notification",
      category: "Communication",
      onClick: noop,
    },
    {
      key: "send-email",
      label: "Send Email",
      icon: <MailOutlined />,
      description: "Compose and send an email",
      category: "Communication",
      onClick: noop,
    },
    {
      key: "create-api-key",
      label: "Create API Key",
      icon: <KeyOutlined />,
      description: "Generate a new API key",
      category: "API",
      onClick: noop,
    },
    {
      key: "export-data",
      label: "Export Data",
      icon: <ExportOutlined />,
      description: "Export data to CSV or JSON",
      category: "Data",
      onClick: noop,
    },
    {
      key: "create-team",
      label: "Create Team",
      icon: <TeamOutlined />,
      description: "Set up a new team",
      category: "Teams",
      onClick: noop,
    },
    {
      key: "add-document",
      label: "Upload Document",
      icon: <FileAddOutlined />,
      description: "Upload a new document",
      category: "Files",
      onClick: noop,
    },
  ];

  const allActions = actions || defaultActions;

  // Group actions by category
  const groupedActions = allActions.reduce((acc, action) => {
    const category = action.category || "Other";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(action);
    return acc;
  }, {} as Record<string, QuickAction[]>);

  const categoryEntries = Object.entries(groupedActions) as [string, QuickAction[]][];
  const items: NonNullable<MenuProps["items"]> = [];

  categoryEntries.forEach(([category, categoryActions], categoryIndex) => {
    items.push({
      key: `category-${category}`,
      type: "group",
      label: (
        <Text strong style={{ fontSize: 12, color: "#8c8c8c" }}>
          {category}
        </Text>
      ),
    });

    categoryActions.forEach((action) => {
      items.push({
        key: action.key,
        label: (
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Space>
              <span style={{ fontSize: 16 }}>{action.icon}</span>
              <div>
                <div>{action.label}</div>
                {action.description && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {action.description}
                  </Text>
                )}
              </div>
            </Space>
            {action.badge && action.badge > 0 && <Badge count={action.badge} />}
          </Space>
        ),
        onClick: () => {
          action.onClick();
          setOpen(false);
        },
        disabled: action.disabled,
      });
    });

    if (categoryIndex < categoryEntries.length - 1) {
      items.push({
        key: `divider-${category}`,
        type: "divider",
      });
    }
  });

  return (
    <Dropdown
      menu={{ items }}
      trigger={["click"]}
      placement={placement}
      open={open}
      onOpenChange={setOpen}
      dropdownRender={(menu: React.ReactNode) => (
        <Card
          style={{
            minWidth: 300,
            maxHeight: 500,
            overflow: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
          bodyStyle={{ padding: 0 }}
        >
          {menu}
        </Card>
      )}
    >
      <Button
        type={buttonType}
        size={buttonSize}
        icon={<ThunderboltOutlined />}
      >
        {buttonText}
      </Button>
    </Dropdown>
  );
};

export default QuickActionsMenu;
